import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

export default function SetuLinks() {
  const [config, setConfig] = useState(null);
  const [amount, setAmount] = useState("10");
  const [note, setNote] = useState("");
  const [result, setResult] = useState(null);
  const [id, setId] = useState("");
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const loadEvents = () => api.get("/setu/test/events").then(r => setEvents(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/setu/test/config").then(r => setConfig(r.data)).catch(e => setError(apiError(e.response?.data?.detail)));
    loadEvents();
  }, []);
  const run = async (create) => {
    setBusy(true); setError("");
    try {
      const response = create
        ? await api.post("/setu/test/create-link", { amount_rupees: Number(amount), note })
        : await api.get(`/setu/test/status/${encodeURIComponent(id.trim())}`);
      setResult(response.data); setId(response.data.id); loadEvents();
    } catch (e) { setError(apiError(e.response?.data?.detail) || "Request failed"); }
    finally { setBusy(false); }
  };
  return <div className="space-y-6" data-testid="setu-page">
    <h2 className="text-2xl font-bold">Setu UMAP sandbox</h2>
    <p className="rounded-lg border p-4">Test environment only. Do not send real money to the sandbox VPA. These results do not credit live wallets or commissions.</p>
    <Card className="p-5 space-y-2">
      <h3 className="font-semibold">Connection</h3>
      <p>{config ? (config.configured ? "Sandbox configured" : "Sandbox configuration needed") : "Loading…"}</p>
      <p>Merchant: <code>{config?.merchant_id || "—"}</code></p>
      <p>VPA: <code>{config?.merchant_vpa || "—"}</code></p>
      <p>Callback: <code className="break-all">{config?.callback_url || "—"}</code></p>
      <p>Callback authentication: {config?.webhook_secret_configured ? "HMAC required" : "Blocked until webhook secret is configured"}</p>
    </Card>
    <Card className="p-5 space-y-4">
      <h3 className="font-semibold">Create sandbox dynamic QR</h3>
      <Label htmlFor="setu-amount">Amount (₹)</Label>
      <Input id="setu-amount" type="number" min="1" max="100000" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} />
      <Label htmlFor="setu-note">Note</Label>
      <Input id="setu-note" maxLength={80} value={note} onChange={e => setNote(e.target.value)} />
      <Button disabled={busy || !config?.configured || !amount} onClick={() => run(true)}>{busy ? "Working…" : "Create sandbox DQR"}</Button>
      {error && <p role="alert" className="text-destructive">{error}</p>}
    </Card>
    <Card className="p-5 space-y-4">
      <h3 className="font-semibold">Payment status</h3>
      <Label htmlFor="setu-dqr">DQR ID</Label>
      <Input id="setu-dqr" value={id} onChange={e => setId(e.target.value)} />
      <Button disabled={busy || !id.trim()} onClick={() => run(false)}>Check with Setu</Button>
      {result && <>
        <p>Payment: <strong>{result.payment_status}</strong> · QR: {result.dqr_status}</p>
        <p className="break-all">Provider intent: <code>{result.intentLink}</code></p>
        <div className="w-fit bg-white p-4"><QRCodeSVG value={result.intentLink} size={200} /></div>
        <p className="text-sm">Sandbox QR — use only the provider’s sandbox testing tools.</p>
        <Button variant="outline" onClick={() => navigator.clipboard.writeText(result.intentLink).then(() => toast.success("Copied sandbox intent")).catch(() => toast.error("Copy failed"))}>Copy sandbox intent</Button>
        <p>Sandbox QR data and provider response:</p>
        <pre className="max-h-64 overflow-auto rounded border p-3 text-xs">{JSON.stringify(result, null, 2)}</pre>
      </>}
    </Card>
    <Card className="p-5 space-y-3">
      <h3 className="font-semibold">Authenticated callback events</h3>
      <Button variant="outline" onClick={loadEvents}>Refresh events</Button>
      {events.length ? events.map(e => <p key={e.event_id}>{e.type} · {e.received_at} · {e.dqr_id}</p>) : <p>No verified events yet.</p>}
    </Card>
  </div>;
}
